/**
 * WebSocket 连接状态枚举
 */
export enum ConnectionState {
  CONNECTING = "CONNECTING",
  CONNECTED = "CONNECTED",
  DISCONNECTING = "DISCONNECTING",
  DISCONNECTED = "DISCONNECTED",
  RECONNECTING = "RECONNECTING",
  ERROR = "ERROR",
}

/**
 * 消息解析器接口
 * @template T 解析后的消息类型
 */
export interface MessageParser<T = any> {
  /**
   * 解析接收到的消息
   * @param data 原始消息数据
   * @returns 解析后的消息
   */
  parse(data: string | ArrayBuffer | Blob): T | null;

  /**
   * 序列化要发送的消息
   * @param message 要发送的消息
   * @returns 序列化后的字符串
   */
  serialize(message: any): string;
}

/**
 * 心跳配置
 */
export interface HeartbeatConfig {
  /** 是否启用心跳 */
  enabled: boolean;
  /** 心跳间隔(毫秒) */
  interval: number;
  /** 心跳超时时间(毫秒) */
  timeout: number;
  /** 心跳消息内容 */
  message: string | (() => string);
  /** 是否需要服务端响应 */
  expectResponse?: boolean;
  /** 服务端响应的消息格式(用于验证) */
  responseValidator?: (data: any) => boolean;
}

/**
 * 重连配置
 */
export interface ReconnectConfig {
  /** 是否启用自动重连 */
  enabled: boolean;
  /** 最大重连次数 */
  maxAttempts: number;
  /** 重连延迟(毫秒),支持指数退避 */
  delay: number;
  /** 最大延迟时间(毫秒) */
  maxDelay: number;
  /** 是否使用指数退避 */
  exponentialBackoff: boolean;
}

/**
 * WebSocket 配置
 */
export interface WebSocketConfig {
  /** WebSocket 服务器 URL */
  url: string;
  /** 消息解析器 */
  parser?: MessageParser;
  /** 心跳配置 */
  heartbeat?: Partial<HeartbeatConfig>;
  /** 重连配置 */
  reconnect?: Partial<ReconnectConfig>;
  /** 是否启用调试日志 */
  debug?: boolean;
  /** WebSocket 协议 */
  protocols?: string | string[];
  /** 连接超时时间(毫秒) */
  connectionTimeout?: number;
}

/**
 * 订阅回调函数
 * @template T 消息数据类型
 */
export type SubscriptionCallback<T = any> = (data: T) => void;

/**
 * 订阅信息
 */
export interface Subscription {
  /** 订阅的主题 */
  topic: string;
  /** 回调函数集合 */
  callbacks: Set<SubscriptionCallback>;
  /** 订阅时间 */
  subscribedAt: number;
  /** 是否已发送订阅请求 */
  sent: boolean;
}

/**
 * WebSocket 事件类型
 */
export enum WebSocketEventType {
  OPEN = "open",
  CLOSE = "close",
  ERROR = "error",
  MESSAGE = "message",
  RECONNECT = "reconnect",
  RECONNECT_FAILED = "reconnect_failed",
  STATE_CHANGE = "state_change",
}

/**
 * WebSocket 事件监听器
 * @template T 事件数据类型
 */
export type WebSocketEventListener<T = any> = (event: T) => void;

/**
 * WebSocket 管理器配置
 */
export interface WebSocketManagerConfig {
  /** 是否为单例模式(每个URL一个实例) */
  singleton?: boolean;
  /** 默认的 WebSocket 配置 */
  defaultConfig?: Partial<WebSocketConfig>;
}

/**
 * 订阅选项
 */
export interface SubscribeOptions {
  /** 是否立即发送订阅请求(如果连接未就绪则等待) */
  immediate?: boolean;
  /** 订阅失败时的重试次数 */
  retries?: number;
  /** 订阅成功的回调 */
  onSuccess?: () => void;
  /** 订阅失败的回调 */
  onError?: (error: Error) => void;
}

/**
 * 发送消息选项
 */
export interface SendOptions {
  /** 是否在连接未就绪时排队 */
  queue?: boolean;
  /** 发送超时时间(毫秒) */
  timeout?: number;
  /** 是否需要等待响应 */
  expectResponse?: boolean;
  /** 响应验证器，用于判断收到的消息是否是该请求的响应 */
  responseValidator?: (response: any, requestId: string) => boolean;
  /** 发送成功的回调 */
  onSuccess?: () => void;
  /** 发送失败的回调 */
  onError?: (error: Error) => void;
  /** 收到响应的回调 */
  onResponse?: (response: any) => void;
}

/**
 * 待处理的请求信息
 */
export interface PendingRequest {
  /** 请求 ID */
  id: string;
  /** 请求消息内容 */
  message: string;
  /** 发送选项 */
  options: SendOptions;
  /** 超时定时器 */
  timeoutTimer?: NodeJS.Timeout;
  /** 发送时间戳 */
  sentAt: number;
}

export interface WSAgentReportMessage {
  channel: string;
  data: WSAgentReportMessageData[];
}

export interface WSAgentReportMessageData {
  body: string;
  title: string;
  extParams: {
    agentId: string;
    level: string;
    id: string;
    assetIds?: string[];
    contents: string;
    todayCount?: number;
  };
}

export interface WSAgentReportMessageDataContent {
  title: string;
  language: string;
  description: string;
  image: string;
  summary: string;
  impact: string;
}
